import { globalTheme } from '@frontend/components/external-providers';
import CloseIcon from '@mui/icons-material/Close';
import { Chip as MuiChip, ChipProps as MuiChipProps } from '@mui/material';
import { styled } from '@mui/system';
import { get } from 'lodash';

interface ChipProps extends MuiChipProps {
  color?: 'primary' | 'secondary' | 'error' | 'default';
  active?: boolean;
  radiusType?: 'tag' | 'chip';
}

const getBackgroundColor = (color = 'primary', disabled?: boolean) => {
  return disabled
    ? get(globalTheme, `palette.background.black.10`, '')
    : color === 'default'
    ? 'white'
    : `${get(globalTheme, `palette.${color}.main`, '')}0D`;
};

const getTextColor = (color = 'primary', disabled?: boolean) => {
  return disabled
    ? get(globalTheme, 'palette.grey.900', '')
    : color === 'default'
    ? get(globalTheme, 'palette.grey.600', '')
    : get(globalTheme, `palette.${color}.main`, '');
};

const getBorderColor = (
  color = 'primary',
  disabled?: boolean,
  active?: boolean,
) => {
  if (disabled) {
    return get(globalTheme, 'palette.background.black.10', '');
  }
  if (active) {
    return get(globalTheme, `palette.${color}.main`, '');
  }
  if (color === 'default') {
    return get(globalTheme, 'palette.grey.600', '');
  }
  return 'transparent';
};

const getHoverBorderColor = (color = 'primary') => {
  if (color === 'default') {
    return get(globalTheme, 'palette.grey.400', '');
  }
  return `${get(globalTheme, `palette.${color}.main`, '')}40`;
};

// 修改 ChipStyled 的樣式設定
const ChipStyled = styled(MuiChip)<{
  $color?: string;
  $disabled?: boolean;
  $radiusType: 'tag' | 'chip';
  $active?: boolean;
}>(({ $color = 'primary', $disabled, $radiusType, $active }) => ({
  backgroundColor: getBackgroundColor($color, $disabled),
  ...($radiusType === 'tag' && { borderRadius: '4px' }),
  color: getTextColor($color, $disabled),
  fontSize: '16px',
  border: `1px solid ${getBorderColor($color, $disabled, $active)}`,
  '&:hover': {
    border: `1px solid ${getHoverBorderColor($color)}`,
  },
  WebkitTextFillColor: 'initial !important',
}));

// 已存在的 getIconColor 函數
const getIconColor = (colorType: string, isDisabled?: boolean) => {
  if (isDisabled) {
    return get(globalTheme, 'palette.grey.600', '');
  }

  const colorMap = {
    error: get(globalTheme, 'palette.error.main', ''),
    primary: get(globalTheme, 'palette.primary.main', ''),
    default: get(globalTheme, 'palette.grey.600', ''),
  } as Record<string, string>;

  return colorMap[colorType] || colorMap['default'];
};

export function Chip({
  color = 'primary',
  radiusType = 'tag',
  active,
  ...props
}: ChipProps) {
  return (
    <ChipStyled
      {...props}
      $color={color}
      $radiusType={radiusType}
      $disabled={props.disabled}
      $active={active}
      deleteIcon={
        <CloseIcon
          style={{
            color: getIconColor(color, props.disabled),
            fontSize: '18',
          }}
        />
      }
    />
  );
}

export default Chip;
